import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageShell";
import { CTASection } from "@/components/site/CTASection";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { canonical, ROOFING_CONTRACTOR_ID, OWNER_ID } from "@/components/site/site-config";
import { BLOG_POSTS } from "@/components/site/constants";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Citrus County Roofing Blog | Destination Roofing" },
      { name: "description", content: "Practical roofing advice from Deston Judy on roof damage, repair, replacement, materials, ventilation, gutters, soffit and wind mitigation." },
      { property: "og:title", content: "Citrus County Roofing Blog | Destination Roofing" },
      { property: "og:description", content: "Practical Florida roofing advice from Deston Judy, owner of Destination Roofing Inc." },
      { property: "og:url", content: canonical("/blog") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/blog") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Blog", href: "/blog" }]),
        {
          "@context": "https://schema.org",
          "@type": "Blog",
          url: canonical("/blog"),
          name: "Destination Roofing — Citrus County Roofing Blog",
          publisher: { "@id": ROOFING_CONTRACTOR_ID },
          blogPost: BLOG_POSTS.map(p => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: canonical(p.slug),
            datePublished: p.datePublished,
            dateModified: p.dateModified,
            author: { "@id": OWNER_ID, "@type": "Person", name: "Deston Judy" },
          })),
        },
      ]),
    }],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Blog", href: "/blog" }]} />
      <PageHero
        eyebrow="Roofing Advice"
        title="Citrus County Roofing Advice from a Local Roofer"
        subtitle="Practical articles from Deston Judy, owner of Destination Roofing Inc. Written from years of walking Florida roofs — not generic content."
      />
      <section className="mx-auto max-w-6xl px-4 py-14 md:py-20">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {BLOG_POSTS.map(p => (
            <article key={p.slug} className="group rounded-xl border border-border bg-white p-6 hover:border-brand transition-colors shadow-[var(--shadow-card)] flex flex-col">
              <h2 className="font-display font-bold text-lg leading-snug">
                <Link to={p.slug} className="hover:text-brand">{p.title}</Link>
              </h2>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{p.excerpt}</p>
              <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground uppercase tracking-wider">
                By Deston Judy · {new Date(p.datePublished).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </div>
              <Link to={p.slug} className="mt-3 text-sm font-semibold text-brand hover:underline">
                Read the article →
              </Link>
            </article>
          ))}
        </div>
      </section>
      <CTASection />
    </>
  );
}
